import { useCallback, useEffect, useState } from 'react';
import { api } from '@verifime/api-definition';
import {
  MultiFileUploader,
  TExistingUploadedFile,
  TFileUploadStatus,
  TFileUploadResult,
  TFileToDelete,
  TFileUploadUrl,
} from '@verifime/components';
import { DocumentType, FileType } from '@verifime/utils';

/**
 * View-layer wrapper for MultiFileUploader that handles /v1/file/* API coordination.
 *
 * This View:
 * - Hardcodes the /v1/file/* API endpoints (no API functions as props)
 * - Manages existing files state
 * - Handles presign → S3 upload → reload flow
 * - Exposes simple props to consumers (entityId, documentType, events)
 *
 * Usage:
 * ```tsx
 * <MultiFileUploaderView
 *   entityId={organisation.id}
 *   documentType={DocumentType.TrustDeed}
 *   onFilesChange={(files) => setHasDocuments(files.length > 0)}
 *   onUploadingChange={(uploading) => setCanSubmit(!uploading)}
 * />
 * ```
 *
 * @see docs/technical-debt.md TD-003 for migration plan
 */

export type MultiFileUploaderViewProps = {
  // Required - identifies upload target
  entityId: string;
  documentType: DocumentType;

  // Configuration (optional)
  label?: string;
  fileTypes?: FileType[];
  maxFileSizeInMB?: number;
  isMultiple?: boolean;
  isReadonly?: boolean;
  isDraggable?: boolean;

  // Events
  onFilesChange?: (files: TExistingUploadedFile[]) => void;
  onUploadingChange?: (isUploading: boolean) => void;
};

export default function MultiFileUploaderView({
  entityId,
  documentType,
  label = 'ADD DOCUMENT',
  fileTypes = [FileType.PNG, FileType.JPG, FileType.JPEG, FileType.PDF],
  maxFileSizeInMB = 100,
  isMultiple = true,
  isReadonly = false,
  isDraggable = false,
  onFilesChange,
  onUploadingChange,
}: MultiFileUploaderViewProps) {
  const [existingUploadedFiles, setExistingUploadedFiles] = useState<TExistingUploadedFile[]>([]);

  // Load existing files from /v1/file/{entityId}/{docType}
  const loadExistingFiles = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const response = await api.getV1fileEntityIdDocType({
          params: { entityId, docType: documentType as any },
          signal,
        });

        if (response && response.itemList) {
          const files = response.itemList
            .toSorted(
              (a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime(),
            )
            .map((item) => ({
              fileName: item.filename,
              fileId: item.fileId,
              fileSizeInBytes: item.sizeInBytes,
            }));

          setExistingUploadedFiles(files);
          onFilesChange?.(files);
        }
      } catch (error) {
        console.error('[MultiFileUploaderView] Failed to load files:', error);
      }
    },
    [entityId, documentType, onFilesChange],
  );

  useEffect(() => {
    const abortController = new AbortController();
    loadExistingFiles(abortController.signal);
    return () => abortController.abort();
  }, [loadExistingFiles]);

  // Get presigned URLs from /v1/file/presign
  const handleRetrieveFileUploadUrls = async (files: File[]): Promise<TFileUploadUrl[]> => {
    try {
      const response = await api.postV1filepresign({
        entityId,
        documentType: documentType as any,
        filenameList: files.map((f) => f.name),
      });

      return response.presignUrlList.map((p) => ({
        fileName: p.filename,
        fileId: p.fileId,
        uploadUrl: p.url,
      }));
    } catch (error) {
      console.error('[MultiFileUploaderView] Failed to get presign URLs:', error);
      throw error;
    }
  };

  // Upload file to S3 using presigned URL
  const handleUploadFile = async (
    fileUploadStatus: TFileUploadStatus,
  ): Promise<TFileUploadResult> => {
    try {
      const response = await fetch(fileUploadStatus.fileUploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': fileUploadStatus.fileType || 'application/octet-stream' },
        body: fileUploadStatus.file,
      });

      if (response.ok) {
        await loadExistingFiles();
        return {
          fileId: fileUploadStatus.fileId,
          fileUploadUrl: response.url,
          isSuccess: true,
          isRemoveFileUploadResult: true,
        };
      }

      return {
        fileId: fileUploadStatus.fileId,
        fileUploadUrl: fileUploadStatus.fileUploadUrl,
        isSuccess: false,
      };
    } catch (error) {
      console.error('[MultiFileUploaderView] Failed to upload file:', error);
      return {
        fileId: fileUploadStatus.fileId,
        fileUploadUrl: fileUploadStatus.fileUploadUrl,
        isSuccess: false,
      };
    }
  };

  // Delete file via /v1/file/{entityId}/{docType}/{fileId}
  const handleDeleteFile = async (
    fileToDelete: TFileToDelete,
    onDeleteCallback?: (success: boolean) => void,
  ) => {
    try {
      await api.deleteV1fileEntityIdDocTypeFileId(undefined, {
        params: {
          fileId: fileToDelete.fileId,
          entityId,
          docType: documentType as any,
        },
      });
      await loadExistingFiles();
      onDeleteCallback?.(true);
    } catch (error) {
      console.error('[MultiFileUploaderView] Failed to delete file:', error);
      onDeleteCallback?.(false);
    }
  };

  return (
    <MultiFileUploader
      label={label}
      fileTypes={fileTypes}
      maxFileSizeInMB={maxFileSizeInMB}
      isMultiple={isMultiple}
      isReadonly={isReadonly}
      isDraggable={isDraggable}
      existingUploadedFiles={existingUploadedFiles}
      isDetectSameFileSelection
      onRetrieveFileUploadUrls={handleRetrieveFileUploadUrls}
      onUploadFile={handleUploadFile}
      onDeleteFile={handleDeleteFile}
      onChange={(statuses) => {
        const isUploading = statuses.some((s) => s.uploadStatus === 'Uploading');
        onUploadingChange?.(isUploading);
      }}
      onAllUploadsDone={() => onUploadingChange?.(false)}
    />
  );
}
